import * as React from 'react';
import { Res } from 'core/api';
import { Binding, Result } from 'core/infrastructure';
import { ifConfirm } from './Dialog';
import { Format, formatAsElementId } from './Utils';
import { DataGrid, DataGridCtor } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';
import classnames from 'classnames';
import Button from './Button';

interface Props<T> extends React.HTMLProps<EditableValueList<T>> {
	gridHeight?: number;
	linkTo: Binding.PropertyPath<T>;
	boundValue?: any[];
	allowReorder?: boolean;
	allowSortOnColumnClick?: boolean;
	columns: DataGridColumn<any>[];
	itemFormat?: Format;
	itemKey: (item: any) => any;
	formId?: string;
	onValueChanged?: (values: any[]) => void;
	exportable?: boolean;
	filtrable?: boolean;
	rowHeightGetter?: (index: number) => number;

	// Factoría usada para crear nuevos elementos
	itemFactory?: () => any;
	// Factoría para copiar elementos;
	copyFactory?: (item: any) => any;

	// Devuelve una promesa con un Result del elemento editado
	// OJO: el item que se pasa a la callback es un clone
	//      del item original, por lo que no hace falta
	//      preocuparse de deshacer los cambios si se cancela
	//      la edición
	// Al recibir también el resto de items se pueden hacer validaciones 
	// cruzadas entre items (por ejemplo, nombres duplicados)
	onEdit?: (item: any, allItems: any[]) => Promise<Result<any>>;
	canDelete?: (items: any[]) => Promise<boolean>;
	confirmDeletionMessage?: string;
	onSelectedItemsChanged?: (selectedItems: any[]) => void;
}

interface State {
	items: any[];
	errorMsg: string;
}

export class EditableValueList<T> extends React.Component<Props<T>, State> implements FormComponent {

	private dataGrid = React.createRef<DataGrid<any>>();

	constructor(props: Props<T>) {
		super(props);

		if (!(props.boundValue instanceof Array))
			throw new Error('EditableValueList esperaba un boundValue de tipo Array');

		this.state = {
			errorMsg: '',
			items: [...props.boundValue]
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<any[]> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.state.items);
	}

	removeItems(items: any[]) {
		this.setState({ items: this.state.items.except(items) });
	}

	async confirm(message: string, action: () => void): Promise<void> {
		return ifConfirm(message, action);
	}

	render() {
		const Grid: DataGridCtor<any> = DataGrid;

		const doEdition = async (itemToEdit: any) => {
			const isNew = !this.state.items.includes(itemToEdit);

			// De momento me conformo con un shallow copy. Se podría hacer
			// un deep copy serializando y deserializando, pero habría que
			// andarse con ojo con las fechas
			const clone = { ...itemToEdit };

			if (this.props.onEdit) {
				const result = await this.props.onEdit(clone, this.state.items);
				if (result.ok) {
					if (isNew) {
						const newItems = [...this.state.items, result.value];
						this.setState({ items: newItems }, () => {
							this.dataGrid.current?.selectItems([result.value]);
							this.props.onValueChanged?.(newItems);
						});
					}
					else {
						Object.assign(itemToEdit, result.value);
						this.forceUpdate(() => this.props.onValueChanged?.(this.state.items));
					}
				}
			}
		};

		const onAddItem = async () => {
			if (this.props.itemFactory) {
				await doEdition(this.props.itemFactory());
			}
		};
		const onEditItem = async () => this.dataGrid.current!.getSelectedItems().length && await doEdition(this.dataGrid.current!.getSelectedItems()[0]);
		const onCopyItem = async () => this.props.copyFactory && this.dataGrid.current!.getSelectedItems().length && await doEdition(this.props.copyFactory(this.dataGrid.current!.getSelectedItems()[0]));

		const removeItemsAfterConfirm = async () => {
			const items = this.dataGrid.current!.getSelectedItems();

			await this.confirm(
				this.props.confirmDeletionMessage ?? Res.Msg.MSG_CONFIRM_DELETION(),
				() => {
					const filteredItems = this.state.items.except(items);
					this.setState({ items: filteredItems }, () => this.props.onValueChanged && this.props.onValueChanged(filteredItems));
				});
		};

		const onRemoveItems = async () => {

			if (this.dataGrid.current!.getSelectedItems().length === 0)
				return;

			const canDelete = this.props.canDelete || ((_items: any[]) => Promise.resolve(true));
			const itemsToDelete = this.dataGrid.current!.getSelectedItems();
			await canDelete(itemsToDelete).then(async canDelete => {
				if (canDelete)
					await removeItemsAfterConfirm();
			});
		};

		const onMoveItemUp = () => {
			if (this.dataGrid.current!.getSelectedItems().length === 0)
				return;

			const selectedItems = [...this.state.items];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index > 0) {
				selectedItems.moveItem(index, index - 1);
				this.setState({ items: selectedItems }, () => this.dataGrid.current!.selectItems([selectedItems[index - 1]]));
			}
		};

		const onMoveItemDown = () => {
			if (this.dataGrid.current!.getSelectedItems().length === 0)
				return;

			const selectedItems = [...this.state.items];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index < selectedItems.length - 1) {
				selectedItems.moveItem(index, index + 1);
				this.setState({ items: selectedItems }, () => this.dataGrid.current!.selectItems([selectedItems[index + 1]]));
			}
		};

		const columns = this.props.columns;

		const orderButtons = <div className='m-t btn-group-vertical'>
			<Button onClick={onMoveItemUp}><i className='text-info fa fa-arrow-up'></i></Button>
			<Button onClick={onMoveItemDown}><i className='text-info fa fa-arrow-down'></i></Button>
		</div>;

		const listButtons = <div className='btn-group-vertical' role='group'>
			{this.props.itemFactory ? <Button onClick={onAddItem}><i className='text-info fa fa-plus'></i></Button> : null}
			{this.props.onEdit ? <Button onClick={onEditItem}><i className='text-info fa fa-pencil-square-o'></i></Button> : null}
			{this.props.copyFactory ? <Button onClick={onCopyItem}><i className='text-info fa fa-copy'></i></Button> : null}
			{<Button onClick={onRemoveItems}><i className='text-info fa fa-trash-o'></i></Button>}
		</div>;

		// Permito ordenar pulsando en las cabeceras del grid si me lo dicen explícitamente,
		// o si no se permite reordenar a mano la lista, porque en general cuando se ordena a mano
		// no queremos que el usuario se cargue la ordenación pulsando sin querer la cabecera
		const isGridSortable = typeof this.props.allowSortOnColumnClick !== 'undefined' ? this.props.allowSortOnColumnClick : !this.props.allowReorder;

		const onGridItemsSorted = () => {
			this.setState({ items: this.dataGrid.current!.getFilteredAndSortedItems() });
		};

		return (
			<div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
				<div className='col-sm-12'>
					<div>
						<ErrorBlock msg={this.state.errorMsg} />

						<div className='multi-select-list'>
							<Grid
								height={this.props.gridHeight || 300}
								itemKey={this.props.itemKey}
								ref={this.dataGrid}
								id={'dataGrid_' + formatAsElementId(this.getPath(), this.props.formId || '')}
								columns={columns}
								initialData={this.state.items}
								shouldPersistState={false}
								sortable={isGridSortable}
								multiSelect={false}
								hideExportButton={!this.props.exportable}
								rowHeightGetter={this.props.rowHeightGetter}
								filtrable={this.props.filtrable}
								maxHeight={this.props.gridHeight || 175}
								onSelectedItemsChanged={this.props.onSelectedItemsChanged}
								onSortChanged={onGridItemsSorted}
								keepSortAfterOnPropsChanged={false} />
						</div>
						<div className='multi-select-list-buttons'>
							{listButtons}
							{this.props.allowReorder ? orderButtons : null}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export type EditableValueListCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
